import { useState, type ReactNode } from 'react';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
  type FilterField,
} from '@/components/common/AdvancedFilter';
import { ExportButton } from '@/components/common/ExportButton';
import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { PageHeader, type PageSearch } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { getFilterPanelProps, type UrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { getActiveStatusFilterField } from '@/utils/filterFields';

const STATUS_ONLY_FILTER_FIELDS: FilterField[] = [getActiveStatusFilterField()];

export interface CatalogueStatCards {
  cards: LabeledStat[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

interface CatalogueListLayoutProps {
  title: string;
  titleAddon?: ReactNode | undefined;
  search: PageSearch;
  filters?: UrlSyncedFilters | undefined;
  filterFields?: FilterField[] | undefined;
  stats?: CatalogueStatCards | undefined;
  onAdd?: (() => void) | undefined;
  addLabel?: string;
  onExport?: (() => Promise<void>) | undefined;
  children: ReactNode;
}

export function CatalogueListLayout({
  title,
  titleAddon,
  search,
  filters,
  filterFields = STATUS_ONLY_FILTER_FIELDS,
  stats,
  onAdd,
  addLabel = 'Add',
  onExport,
  children,
}: CatalogueListLayoutProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const hasActions = filters !== undefined || onExport !== undefined;

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title={title}
        titleAddon={titleAddon}
        search={search}
        actions={
          hasActions ? (
            <>
              {filters ? (
                <AdvancedFilterButton
                  isOpen={isFilterOpen}
                  activeCount={filters.activeFilterCount}
                  onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
                />
              ) : null}
              {onExport ? <ExportButton onExport={onExport} /> : null}
            </>
          ) : null
        }
        createAction={onAdd ? { label: addLabel, onClick: onAdd } : undefined}
      />

      {filters ? (
        <FilterPanelCollapse isOpen={isFilterOpen}>
          <AdvancedFilterPanel fields={filterFields} {...getFilterPanelProps(filters)} />
        </FilterPanelCollapse>
      ) : null}

      {stats && !stats.isError ? (
        <StatCardGrid
          stats={stats.cards}
          isLoading={stats.isLoading}
          isFetching={stats.isFetching}
        />
      ) : null}

      {children}
    </div>
  );
}
